import React from 'react';
import { Link as MuiLink, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import type { PassEligibleTutor } from '../../models/Pass';
import { PassAudience, passEligibleTutorPath } from '../../utils/passUsageBookingPath';
import RoomBookingUserAvatar from '../RoomBookingUserAvatar';
import { PASS_IDENTITY_AVATAR_SIZE } from './passDetailLayout';

export interface PassEligibleTutorsRowProps {
  tutors: PassEligibleTutor[];
  audience: PassAudience;
  /** Override the uppercase row heading (default: Eligible tutors). */
  heading?: string;
  /** Stop click bubbling (e.g. wallet card CardActionArea). */
  stopPropagation?: boolean;
}

/** Eligible tutors under a LESSON_OPTION pass target, styled like eligible rooms. */
const PassEligibleTutorsRow: React.FC<PassEligibleTutorsRowProps> = ({
  tutors,
  audience,
  heading,
  stopPropagation = false,
}) => {
  const { t } = useTranslation();
  if (tutors.length === 0) return null;

  return (
    <Stack spacing={0.75}>
      {heading === '' ? null : (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ textTransform: 'uppercase', letterSpacing: '0.06em' }}
        >
          {heading || t('userPasses.details.eligibleTutors', 'Eligible tutors')}
        </Typography>
      )}
      <Stack direction="row" spacing={1.25} useFlexGap flexWrap="wrap" alignItems="center">
        {tutors.map((tutor) => {
          const href = passEligibleTutorPath(audience, tutor.tutorId);
          const label = tutor.name?.trim() || t('userPasses.details.unknownTutor', 'Tutor');
          const content = (
            <>
              <RoomBookingUserAvatar
                userProfileImageUrl={tutor.profileImageUrl}
                displayName={tutor.name || ''}
                size={PASS_IDENTITY_AVATAR_SIZE}
              />
              <Typography variant="body2" fontWeight={500} color="primary.main">
                {label}
              </Typography>
            </>
          );

          return (
            <MuiLink
              key={tutor.tutorId}
              component={RouterLink}
              to={href}
              underline="hover"
              color="inherit"
              onClick={stopPropagation ? (event) => event.stopPropagation() : undefined}
              onMouseDown={stopPropagation ? (event) => event.stopPropagation() : undefined}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                minWidth: 0,
              }}
            >
              {content}
            </MuiLink>
          );
        })}
      </Stack>
    </Stack>
  );
};

export default PassEligibleTutorsRow;
